import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import pluralize from 'pluralize';

import useOpenAlert from '../../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../../util/normalize-errors';
import { AccountPhotoManagerSubscriptions } from '../../../../../../../../graphql/queries/account';
import {
  accountPaymentMethodCreate as accountPaymentMethodCreateMutation,
  checkoutPhotoFinderCredits as checkoutPhotoFinderCreditsMutation,
} from '../../../../../../../../graphql/mutations/checkout';
import getPhotoFinderCreditItems from '../queries/getPhotoFinderCreditItems';
import { gtm } from '../../../../../../../../util/analytics';
import { formatPriceForDisplay } from '../../../../../../../../util/format-money';

const usePhotoFinderCreditItemsCheckout = ({ onCheckoutSuccess }) => {
  const openAlert = useOpenAlert();
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { data: { me } = {}, refetch: refetchAccount } = useQuery(
    AccountPhotoManagerSubscriptions,
    {
      options: {
        fetchPolicy: 'network-only',
      },
    },
  );
  const { data: { photoFinderCreditItems: creditItems = [] } = {} } = useQuery(
    getPhotoFinderCreditItems,
    {
      fetchPolicy: 'cache-first',
    },
  );
  const [checkout] = useMutation(checkoutPhotoFinderCreditsMutation);
  const [accountPaymentMethodCreate] = useMutation(
    accountPaymentMethodCreateMutation,
  );

  const onCheckout = async ({ stripeToken }) => {
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);
      const { data: { checkoutPhotoFinderCredits } = {} } = await checkout({
        variables: {
          input: {
            photo_finder_credit_item_id: selectedItem.id,
            email_address: stripeToken?.email,
            stripe_token: stripeToken?.id,
            stripe_payment_method_id: stripeToken?.stripe_payment_method_id,
            stripe_connect_account_id: stripeToken?.stripe_connect_account_id,
            platform: 'WEB',
          },
        },
      });

      gtm.trackCustomEvent({
        event: 'PhotoFinderCreditsPurchase',
        package: `PF_ITEM_${selectedItem?.amount}`,
        value: formatPriceForDisplay(selectedItem?.price?.value),
        user_id: me?.id,
      });

      if (
        !me?.default_payment_method &&
        stripeToken?.id &&
        stripeToken?.email
      ) {
        await accountPaymentMethodCreate({
          variables: {
            input: {
              stripe_token: stripeToken.id,
              email_address: stripeToken.email,
            },
          },
        });
      }

      if (checkoutPhotoFinderCredits?.success) {
        openAlert({
          type: 'success',
          title: 'Success!',
          message: (
            <p>
              <span className="text-black">{selectedItem.amount}</span>{' '}
              {pluralize('credit', selectedItem.amount)} have been added to your
              account!
            </p>
          ),
        });
        await refetchAccount?.();
        await onCheckoutSuccess?.();
      }
      setSuccess(true);
    } catch (e) {
      openAlert(getErrorString(e));
    } finally {
      setSubmitting(false);
    }
  };

  return {
    account: me,
    creditItems,
    selectedItem,
    setSelectedItem,
    submitting,
    success,
    onCheckout,
  };
};

export default usePhotoFinderCreditItemsCheckout;
